import type {
  ExperiencePayloadEntry,
  SavedExperience,
} from '@/components/experience-repeater';

/**
 * Pet Walker module-details request/response shapes.
 * Mirrors backend/src/modules/walker/walker.validators.ts.
 */

export type WalkerGender = 'MALE' | 'FEMALE' | 'OTHER';

export type WalkerServiceRadius =
  | 'KM_1'
  | 'KM_3'
  | 'KM_5'
  | 'KM_10'
  | 'KM_15';

export type WalkerDetailsRequest = {
  full_name: string; // 2..120
  date_of_birth: string; // ISO YYYY-MM-DD, walker must be 18..100
  gender?: WalkerGender;
  service_radius: WalkerServiceRadius;
  years_of_experience: number; // 0..80 integer
  specialization_codes: string[]; // 1..10 unique
  language_codes: string[]; // 1..10 unique
  /**
   * Prior jobs. Optional — omitting it leaves the stored list alone; sending a list
   * REPLACES it, so the form always submits every row it holds.
   */
  experience?: ExperiencePayloadEntry[];
};

export type WalkerDetailsResponse = {
  id: string;
  vendor_profile_id: string;
  full_name: string;
  date_of_birth: string;
  gender: WalkerGender | null;
  service_radius: WalkerServiceRadius;
  years_of_experience: number;
  specialization_codes: string[];
  language_codes: string[];
  created_at: string;
  updated_at: string;
  /** Stored prior jobs for this module, newest first. */
  experience: SavedExperience[];
};

/**
 * Hard-coded mirror of backend/prisma/seed.ts WALKER_SPECIALIZATIONS.
 * Wave 6 polish replaces this with GET /api/v1/lookups/walker-specializations.
 */
export const WALKER_SPECIALIZATION_OPTIONS: Array<{
  code: string;
  display_name: string;
}> = [
  { code: 'DOG_WALKING', display_name: 'Dog Walking' },
  { code: 'CAT_WALKING', display_name: 'Cat Walking' },
  { code: 'SENIOR_PETS', display_name: 'Senior Pets' },
  { code: 'LARGE_BREEDS', display_name: 'Large Breeds' },
  { code: 'SMALL_BREEDS', display_name: 'Small Breeds' },
  { code: 'PUPPIES', display_name: 'Puppies' },
  { code: 'SPECIAL_NEEDS', display_name: 'Special Needs' },
];

/**
 * Hard-coded mirror of backend/prisma/seed.ts LANGUAGES.
 * Wave 6 polish replaces this with GET /api/v1/lookups/languages.
 */
export const LANGUAGE_OPTIONS: Array<{ code: string; display_name: string }> = [
  { code: 'en', display_name: 'English' },
  { code: 'hi', display_name: 'Hindi' },
  { code: 'ta', display_name: 'Tamil' },
  { code: 'te', display_name: 'Telugu' },
  { code: 'mr', display_name: 'Marathi' },
  { code: 'bn', display_name: 'Bengali' },
  { code: 'gu', display_name: 'Gujarati' },
  { code: 'kn', display_name: 'Kannada' },
  { code: 'pa', display_name: 'Punjabi' },
  { code: 'ur', display_name: 'Urdu' },
  { code: 'ml', display_name: 'Malayalam' },
];

export const WALKER_SERVICE_RADIUS_OPTIONS: Array<{
  value: WalkerServiceRadius;
  label: string;
}> = [
  { value: 'KM_1', label: '1 km' },
  { value: 'KM_3', label: '3 km' },
  { value: 'KM_5', label: '5 km' },
  { value: 'KM_10', label: '10 km' },
  { value: 'KM_15', label: '15 km' },
];

export const WALKER_GENDER_OPTIONS: Array<{
  value: WalkerGender;
  label: string;
}> = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];
